import { FIREBASE_DATABASE_URL } from '../config/firebase';
import { buildUsdPrizesFromFunding } from '../utils/prizePoolData';
import { authFetch } from './authFetch';
import { fetchHotaLeaderboard, findHotaLeaderboardRank } from './hotaMeta';

export const getTournamentPrizeLabel = (tournament) => {
    if (!tournament) {
        return null;
    }

    const formatPool = (amount) => {
        const value = Number(amount);
        if (!Number.isFinite(value) || value <= 0) {
            return null;
        }
        return `$${value.toFixed(1)} prize pool`;
    };

    const collected = Number(tournament.communityFundingUsd) || 0;
    if (collected > 0) {
        return formatPool(collected);
    }

    if (tournament.prizeType === 'money' && tournament.totalPrizeUsd) {
        return formatPool(tournament.totalPrizeUsd);
    }
    if (tournament.pricePull && typeof tournament.pricePull === 'object') {
        const total = Object.values(tournament.pricePull).reduce((sum, v) => sum + Number(v || 0), 0);
        if (total > 0) {
            return formatPool(total);
        }
    }
    return null;
};

// Progress tracking for tournament games
export async function getGameProgress(gameId) {
    try {
        const response = await fetch(`${FIREBASE_DATABASE_URL}/games/heroes3/${gameId}/progress.json`);
        if (!response.ok) {
            return null;
        }
        return await response.json();
    } catch (e) {
        console.error('Error fetching game progress:', e);
        return null;
    }
}

export async function setGameProgress(gameId, progress) {
    try {
        const response = await authFetch(`${FIREBASE_DATABASE_URL}/games/heroes3/${gameId}/progress.json`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(progress)
        });
        return response.ok;
    } catch (e) {
        console.error('Error setting game progress:', e);
        return false;
    }
}

// Pair-level progress tracking for tournament submit sessions
// Stores resumable state per match (tournamentId + stageIndex + pairIndex)
export async function getPairProgress(tournamentId, pairId) {
    try {
        const response = await fetch(
            `${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}/submitProgress/${encodeURIComponent(pairId)}.json`
        );
        if (!response.ok) {
            return null;
        }
        return await response.json();
    } catch (e) {
        console.error('Error fetching pair progress:', e);
        return null;
    }
}

export async function savePairProgress(tournamentId, pairId, data) {
    try {
        const url = `${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}/submitProgress/${encodeURIComponent(pairId)}.json`;
        const response = await authFetch(url, {
            method: data === null ? 'DELETE' : 'PUT',
            headers: data === null ? {} : { 'Content-Type': 'application/json' },
            body: data === null ? undefined : JSON.stringify(data)
        });
        return response.ok;
    } catch (e) {
        console.error('Error saving pair progress:', e);
        return false;
    }
}

// PATCH a single per-castle key (e.g. 'g0_c1') into the progress document
export async function updatePairProgressCastle(tournamentId, pairId, castleKey) {
    try {
        const url = `${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}/submitProgress/${encodeURIComponent(pairId)}.json`;
        const response = await authFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ [castleKey]: true })
        });
        return response.ok;
    } catch (e) {
        console.error('Error updating castle progress:', e);
        return false;
    }
}

// PATCH only the latestStage field without overwriting form data saved by the modal
export async function updatePairProgressStage(tournamentId, pairId, stage) {
    try {
        const url = `${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}/submitProgress/${encodeURIComponent(pairId)}.json`;
        const response = await authFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ latestStage: stage })
        });
        return response.ok;
    } catch (e) {
        console.error('Error updating pair progress stage:', e);
        return false;
    }
}
export const getKonoplayLatestRating = (user) => {
    const rating = user?.ratings;
    if (typeof rating === 'string' && rating.includes(',')) {
        return parseFloat(rating.split(',').at(-1)) || 0;
    }
    return parseFloat(rating) || 0;
};

export const fetchKonoplayLeaderboardRank = async (player) => {
    try {
        const response = await fetch(`${FIREBASE_DATABASE_URL}/users.json`);
        if (!response.ok) {
            throw new Error('Unable to fetch leaderboard.');
        }
        const data = await response.json();
        if (!data || !player?.enteredNickname) {
            return null;
        }

        const usersArray = Object.values(data).filter((user) => user && user.ratings !== undefined);
        usersArray.sort((a, b) => getKonoplayLatestRating(b) - getKonoplayLatestRating(a));

        const place = usersArray.findIndex((user) => user.enteredNickname === player.enteredNickname) + 1;
        return place > 0 ? place : null;
    } catch (error) {
        console.error('Error fetching Konoplay leaderboard rank:', error);
        return null;
    }
};

export const fetchLeaderboard = async (player) => {
    if (!player?.enteredNickname) {
        return null;
    }

    try {
        const hotaLeaderboard = await fetchHotaLeaderboard({ limit: 500 });
        const hotaRank = findHotaLeaderboardRank(player.enteredNickname, hotaLeaderboard);
        if (hotaRank != null) {
            return hotaRank;
        }
    } catch (error) {
        console.error('HotA leaderboard rank lookup failed, using Konoplay fallback:', error);
    }

    return fetchKonoplayLeaderboardRank(player);
};

export const confirmWindow = (message) => {
    const response = window.confirm(message);
    if (response) {
        console.log('YES');
    } else {
        console.log('NO');
    }
    return response;
};

const MIN_PLAYER_RATING = 0.5;

const enforceMinPlayerRating = (ratingValue) => {
    const numericRating = Number(ratingValue);
    if (!Number.isFinite(numericRating)) {
        return MIN_PLAYER_RATING;
    }
    return Math.max(MIN_PLAYER_RATING, numericRating);
};

export const addScoreToUser = async (userId, data, scoreToAdd, winner, tournamentId, team) => {
    const { games, ratings } = data;
    const safeScoreToAdd = enforceMinPlayerRating(scoreToAdd);

    let updatedRatings = ratings + `, ${safeScoreToAdd.toFixed(2)}`;

    console.log('updatedRatings', updatedRatings);

    try {
        const tournamentPlayerResponse = await fetch(
            `${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}/players/.json`
        );

        const tournamentData = await tournamentPlayerResponse.json();

        console.log('tournamentData', tournamentData);

        const result = findByName(tournamentData, team, safeScoreToAdd);

        if (result?.id && tournamentData.hasOwnProperty(result.id)) {
            tournamentData[result.id].ratings += `, ${safeScoreToAdd.toFixed(2)}`;

            try {
                const userResponse = await authFetch(`${FIREBASE_DATABASE_URL}/users/${userId}.json`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                        gamesPlayed: {
                            heroes3: {
                                total: games.heroes3.total + 1,
                                win: userId === winner ? games.heroes3.win + 1 : games.heroes3.win,
                                lose: userId === winner ? games.heroes3.lose : games.heroes3.lose + 1
                            }
                        },
                        ratings: updatedRatings
                        // Stars stay frozen until the tournament is marked finished.
                    }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                if (userResponse.ok) {
                    console.log(`✓ ${team} rating updated successfully to ${safeScoreToAdd.toFixed(2)}`);
                } else {
                    console.error(`✗ Failed to update ${team} rating. Status: ${userResponse.status}`);
                }
            } catch (error) {
                console.error(`✗ Error updating ${team} rating:`, error);
            }
        }

        // console.log('tournamentData', tournamentData);

        let userResponse = await authFetch(
            `${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}/players/.json`,
            {
                method: 'PUT',
                body: JSON.stringify(tournamentData),
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        return await userResponse.json();
    } catch (error) {
        console.error(error);
    }
};

export const findByName = (data, nickname, _newRating) => {
    for (let key in data) {
        if (data[key].name === nickname) {
            // data[key].ratings = newRating;
            return { id: key, ...data[key] };
        }
    }
    return null; // Return null if no match is found
};

export const lookForUserId = async (nickname, full) => {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/users.json`, {
        method: 'GET'
    });

    const data = await response.json();

    const userObj = Object.entries(data).find(([, obj]) => obj.enteredNickname === nickname);

    if (full === 'full') {
        if (userObj) {
            return { name: userObj[1].enteredNickname };
        }
    }

    if (userObj) {
        return userObj[0]; // Return the ID of the matching user object
    } else {
        return null; // Return null if no matching user object is found
    }
};

export const loadUserById = async (userId) => {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/users/${userId}.json`, {
        method: 'GET'
    });

    const data = await response.json();

    return data;
};

export const lookForUserPrevScore = async (userId) => {
    let results = {};
    const response = await fetch(`${FIREBASE_DATABASE_URL}/users/${userId}.json`, {
        method: 'GET'
    });

    const data = await response.json();

    if (data && !!data.ratings) {
        results.ratings = data.ratings;
        results.games = data.gamesPlayed;
        results.stars = data.stars;
    } else {
        await authFetch(`${FIREBASE_DATABASE_URL}/users/${userId}/ratings.json`, {
            method: 'PUT',
            body: JSON.stringify(0)
        });
    }
    if (data && data.gamesPlayed) {
        results.games = data.gamesPlayed;
    } else {
        await authFetch(`${FIREBASE_DATABASE_URL}/users/${userId}/gamesPlayed.json`, {
            method: 'PUT',
            body: JSON.stringify({ heroes3: { total: 0, win: 0, lose: 0 } })
        });
    }
    return results;
};

export const fetchCastlesList = async () => {
    try {
        const response = await fetch(`${FIREBASE_DATABASE_URL}/statistic/heroes3/castles.json`);
        const data = await response.json();

        let castles = Object.entries(data).map(([id, castle]) => ({
            id: id,
            name: id,
            win: castle.win,
            lose: castle.lose,
            total: castle.total,
            rate: castle.total !== 0 ? (castle.win / castle.total) * 100 : 0
        }));

        castles.sort((a, b) => b.rate - a.rate);

        return castles;
    } catch (error) {
        console.error(error);
    }
};

export const lookForCastleStats = async (castle, action) => {
    let body;
    const response = await fetch(`${FIREBASE_DATABASE_URL}/statistic/heroes3/castles/${castle}.json`, {
        method: 'GET'
    });

    const castleData = await response.json();

    // Initialize castle data if it doesn't exist
    const currentStats = castleData || { total: 0, win: 0, lose: 0 };

    if (action === 'win') {
        body = JSON.stringify({
            total: currentStats.total + 1,
            win: currentStats.win + 1,
            lose: currentStats.lose
        });
    } else {
        body = JSON.stringify({
            total: currentStats.total + 1,
            win: currentStats.win,
            lose: currentStats.lose + 1
        });
    }

    try {
        const response = await authFetch(`${FIREBASE_DATABASE_URL}/statistic/heroes3/castles/${castle}.json`, {
            method: 'PATCH',
            body: body,
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return await response.json();
    } catch (error) {
        console.error(error);
    }
};

export const getRating = async (opponentId) => {
    let rating;
    const response = await fetch(`${FIREBASE_DATABASE_URL}/users/${opponentId}/gamesPlayed/heroes3.json`, {
        method: 'GET'
    });

    const data = await response.json();

    if (data) {
        const totalGames = data.total;
        const victories = data.total - data.lose;
        const winRatio = victories / totalGames;

        rating = winRatio * 5 + totalGames * 0.5;
    }

    return rating;
};

export const getNewRating = (playerRating, opponentRating, didWin, kFactor = 1) => {
    // ELO expected score.
    // Divisor is tuned for this game's rating scale (~3–20 range).
    // A 5-point difference gives ~75% expected win for the higher-rated player.
    const expectedScore = 1 / (1 + Math.pow(10, (opponentRating - playerRating) / 10));

    // Standard ELO actual scores: 1 for a win, 0 for a loss.
    // Previously 0.7/0.3 which incorrectly gave rating gains even after a loss.
    const actualScore = didWin ? 1 : 0;

    // Rating change: positive when you outperform expectation, negative when you underperform.
    // Beating a much stronger player → big gain. Losing to a much weaker player → big loss.
    let ratingChange = kFactor * (actualScore - expectedScore);

    // Symmetric cap to prevent extreme swings (e.g. on first game with rating 0).
    ratingChange = Math.max(-2.5, Math.min(ratingChange, 2.5));

    return enforceMinPlayerRating(playerRating + ratingChange);
};

export const SITE_STARS_MAX = 5;
export const SITE_STARS_MIN = 0.5;
export const SITE_STARS_TOP50_COUNT = 50;
export const SITE_STARS_TOP50_MIN = 4;

export const roundSiteStars = (value) => {
    const clamped = Math.max(SITE_STARS_MIN, Math.min(SITE_STARS_MAX, value));
    return Math.round(clamped * 2) / 2;
};

export const computeSiteStarsFromRank = (rank, { topCount = SITE_STARS_TOP50_COUNT } = {}) => {
    if (rank == null || rank <= 0) {
        return SITE_STARS_MIN;
    }

    if (rank <= topCount) {
        const progress = (rank - 1) / Math.max(topCount - 1, 1);
        return roundSiteStars(SITE_STARS_MAX - progress * (SITE_STARS_MAX - SITE_STARS_TOP50_MIN));
    }

    if (rank <= 150) {
        const progress = (rank - topCount) / (150 - topCount);
        return roundSiteStars(SITE_STARS_TOP50_MIN - progress * 1);
    }

    if (rank <= 500) {
        const progress = (rank - 150) / (500 - 150);
        return roundSiteStars(3 - progress * 1.5);
    }

    return SITE_STARS_MIN;
};

export const applySiteStarsFromRank = (players) =>
    players.map((player) => ({
        ...player,
        siteStars: computeSiteStarsFromRank(player.rank)
    }));

export const getSiteStarBounds = (players, ratingKey = 'rating') => {
    const ratings = players
        .map((player) => Number(player?.[ratingKey]))
        .filter((rating) => Number.isFinite(rating) && rating > 0);

    if (!ratings.length) {
        return { highest: 0, lowest: 0 };
    }

    return {
        highest: Math.max(...ratings),
        lowest: Math.min(...ratings)
    };
};

export const computeSiteStars = (rating, bounds, minStars = SITE_STARS_MIN) => {
    if (!bounds || (!bounds.highest && !bounds.lowest)) {
        return minStars;
    }

    return calculateStarsFromRating(Number(rating) || 0, bounds.highest, bounds.lowest, minStars);
};

export const applySiteStarsToPlayers = (players, bounds, ratingKey = 'rating') =>
    players.map((player) => ({
        ...player,
        siteStars: computeSiteStars(player?.[ratingKey], bounds)
    }));

export const calculateStarsFromRating = (rating, highestRating, lowestRating, minStars = SITE_STARS_MIN) => {
    let cappedStars;
    if (rating > 0) {
        const totalStars = SITE_STARS_MAX;
        const range = highestRating - lowestRating;

        // Linear distribution from 0.5 to 5.0 stars
        const normalized = range === 0 ? 1 : (rating - lowestRating) / range;
        const rawStars = normalized * (totalStars - minStars) + minStars;

        console.log(
            `calculateStarsFromRating: rating=${rating}, highest=${highestRating}, lowest=${lowestRating}, minStars=${minStars}, normalized=${normalized}, rawStars=${rawStars}`
        );

        cappedStars = Math.round(rawStars * 2) / 2; // Round to nearest 0.5
        cappedStars = Math.min(cappedStars, totalStars); // Cap stars at 5

        console.log(`Final capped stars: ${cappedStars}`);
    } else {
        cappedStars = minStars;
    }

    if (+cappedStars < minStars) {
        cappedStars = minStars;
    }

    return cappedStars;
};

export const getStarImageFilename = (stars) => {
    const imageName = '../../image/ratings/' + stars + '.png';
    return imageName;
};

export const updateRating = async (opponentId, rating, _game) => {
    //TODO: make ratings by game
    const ratingResponse = await authFetch(`${FIREBASE_DATABASE_URL}/users/${opponentId}/ratings.json`, {
        method: 'PUT',
        body: rating,
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (ratingResponse.ok) {
        console.log('rate updated');
    }
};
export const updateAvatar = async (userId, avatar) => {
    const avatarResponse = await authFetch(`${FIREBASE_DATABASE_URL}/users/${userId}/.json`, {
        method: 'PATCH',
        body: JSON.stringify({ avatar }),
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (avatarResponse.ok) {
        console.log('avatar updated');
    }
};

export const getAvatar = async (userId) => {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/users/${userId}/avatar.json`);

    if (response.ok) {
        const data = await response.json();
        return data;
    } else {
        throw new Error('Failed to get avatar data');
    }
};

export const updateStars = async (opponentId, stars) => {
    //TODO: make ratings by game
    const ratingResponse = await authFetch(`${FIREBASE_DATABASE_URL}/users/${opponentId}/stars.json`, {
        method: 'PUT',
        body: stars,
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (ratingResponse.ok) {
        console.log('rate updated');
    }
};

//IS NOT WORKING DUE TO CORS ISSUE
export const getAllUsers = async () => {
    const ratingResponse = await fetch(`${FIREBASE_DATABASE_URL}/users/`, {
        method: 'GET',
        origin: ['*']
    });

    if (ratingResponse.ok) {
        return ratingResponse.json();
    }
};

export const lookForTournamentName = async (tournamentId) => {
    const tournamentResponse = await fetch(`${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}.json`, {
        method: 'GET',
        origin: ['*']
    });

    if (tournamentResponse.ok) {
        return tournamentResponse.json();
    }
};

export const determineTournamentPrizes = (total_prize) => {
    let prizes = {};
    // Calculate prize amounts for each place
    prizes['1st Place'] = (total_prize * 0.6).toFixed(2);
    prizes['2nd Place'] = (total_prize * 0.3).toFixed(2);
    prizes['3rd Place'] = (total_prize * 0.1).toFixed(2);
    return prizes;
};

export const pullTournamentPrizes = async (tournamentId) => {
    const tournamentResponse = await fetch(`${FIREBASE_DATABASE_URL}/tournaments/heroes3/${tournamentId}.json`);
    if (!tournamentResponse.ok) {
        return null;
    }

    const tournament = await tournamentResponse.json();
    const goldPrizes = buildUsdPrizesFromFunding(tournament);
    if (goldPrizes) {
        return goldPrizes;
    }

    if (tournament?.pricePull) {
        return tournament.pricePull;
    }

    return null;
};

export const getPlayerPrizeTotal = async (userId) => {
    const userResponse = await fetch(
        `${FIREBASE_DATABASE_URL}/users/${userId}/totalPrize.json`
        // {
        //     method: 'GET',
        //     origin: ['*']
        // }
    );
    if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('getPlayerPrizeTotal - to get user prize', userData);
        return userData;
    } else {
        console.log('Failed getPlayerPrizeTotal - to get user prize data');
    }
};

export async function fetchLastGamesForPlayer(playerName, count = 5) {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/games/heroes3.json`);
    if (!response.ok) {
        return [];
    }
    const data = await response.json();
    if (!data) {
        return [];
    }

    // Flatten and filter games where the player participated
    const games = Object.values(data).filter((g) => g.opponent1 === playerName || g.opponent2 === playerName);

    // Sort by date descending and take last N
    return games
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, count)
        .map((g) => ({
            date: g.date,
            opponent: g.opponent1 === playerName ? g.opponent2 : g.opponent1,
            result: g.winner === playerName ? 'Win' : 'Loss'
        }));
}

/**
 * Returns the best and worst castles for a player based on win/loss count.
 * @param {string} playerName
 * @returns {Promise<{ best: { castle: string, wins: number }, worst: { castle: string, loses: number } }>}
 */
export async function fetchBestAndWorstCastleForPlayer(playerName) {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/games/heroes3.json`);
    if (!response.ok) {
        return { best: null, worst: null };
    }
    const data = await response.json();
    if (!data) {
        return { best: null, worst: null };
    }

    // Collect all games where the player participated
    const games = Object.values(data).filter((g) => g.opponent1 === playerName || g.opponent2 === playerName);

    // Count wins and loses by castle
    const castleStats = {};

    games.forEach((g) => {
        let playerCastle, isWin;

        if (g.opponent1 === playerName) {
            playerCastle = g.opponent1Castle;
            isWin = g.winner === playerName;
        } else {
            playerCastle = g.opponent2Castle;
            isWin = g.winner === playerName;
        }

        // Handle nested games if castle is missing
        if (!playerCastle) {
            if (g.gameType && g.games && Array.isArray(g.games)) {
                g.games.forEach((subGame) => {
                    let subCastle, subIsWin;

                    if (g.opponent1 === playerName) {
                        subCastle = subGame.castle1;
                        subIsWin = subGame.gameWinner === playerName;
                    } else {
                        subCastle = subGame.castle2;
                        subIsWin = subGame.gameWinner === playerName;
                    }

                    if (!subCastle) {
                        console.error(`[${playerName}] Sub-game not counted due to missing castle:`, subGame);
                        return;
                    }

                    if (!castleStats[subCastle]) {
                        castleStats[subCastle] = { wins: 0, loses: 0 };
                    }

                    if (subIsWin) {
                        castleStats[subCastle].wins += 1;
                    } else {
                        castleStats[subCastle].loses += 1;
                    }
                });
                return; // Skip the rest since we handled it with subgames
            }

            // Log error for games that weren't calculated due to missing castle info
            console.error(`[${playerName}] Game not counted due to missing castle:`, g);
            return;
        }

        // Handle normal games
        if (!castleStats[playerCastle]) {
            castleStats[playerCastle] = { wins: 0, loses: 0 };
        }

        if (isWin) {
            castleStats[playerCastle].wins += 1;
        } else {
            castleStats[playerCastle].loses += 1;
        }
    });

    // Find best (most wins) and worst (most loses) castles, including both wins and loses
    let best = null,
        worst = null;
    for (const [castle, stats] of Object.entries(castleStats)) {
        if (!best || stats.wins > best.wins) {
            best = { castle, wins: stats.wins, loses: stats.loses };
        }
        if (!worst || stats.loses > worst.loses) {
            worst = { castle, wins: stats.wins, loses: stats.loses };
        }
    }

    // Log summary of wins and loses
    const totalWins = Object.values(castleStats).reduce((sum, s) => sum + s.wins, 0);
    const totalLoses = Object.values(castleStats).reduce((sum, s) => sum + s.loses, 0);
    const totalGames = games.length;
    console.log(
        `[${playerName}] Total games: ${totalGames}, Wins: ${totalWins}, Loses: ${totalLoses}, Wins+Loses: ${totalWins + totalLoses}`
    );
    if (totalWins + totalLoses !== totalGames) {
        console.warn(
            `[${playerName}] Warning: Wins + Loses != Total games. Possible missing castle info or data inconsistency.`
        );
    }

    return { best, worst };
}

/**
 * Returns full castle statistics for a player: wins and loses for each castle.
 * @param {string} playerName
 * @returns {Promise<Object>} An object like { [castleName]: { wins: number, loses: number } }
 */
export async function fetchFullCastleStatsForPlayer(playerName) {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/games/heroes3.json`);
    if (!response.ok) {
        return {};
    }
    const data = await response.json();
    if (!data) {
        return {};
    }

    // Collect all games where the player participated
    const games = Object.values(data).filter((g) => g.opponent1 === playerName || g.opponent2 === playerName);

    // Count wins and loses by castle
    const castleStats = {};

    games.forEach((g) => {
        let playerCastle, isWin;

        if (g.opponent1 === playerName) {
            playerCastle = g.opponent1Castle;
            isWin = g.winner === playerName;
        } else {
            playerCastle = g.opponent2Castle;
            isWin = g.winner === playerName;
        }
        console.log('g', g);

        // Handle nested games if castle is missing
        if (!playerCastle) {
            console.log('g.gameType', g.gameType);

            if (g.gameType && g.games && Array.isArray(g.games)) {
                g.games.forEach((subGame) => {
                    console.log('subGame', subGame);
                    let subCastle, subIsWin;

                    if (g.opponent1 === playerName) {
                        subCastle = subGame.castle1;
                        subIsWin = subGame.gameWinner === playerName;
                    } else {
                        subCastle = subGame.castle2;
                        subIsWin = subGame.gameWinner === playerName;
                    }

                    if (!subCastle) {
                        console.error(`[${playerName}] Sub-game not counted due to missing castle:`, subGame);
                        return;
                    }

                    if (!castleStats[subCastle]) {
                        castleStats[subCastle] = { wins: 0, loses: 0 };
                    }

                    if (subIsWin) {
                        castleStats[subCastle].wins += 1;
                    } else {
                        castleStats[subCastle].loses += 1;
                    }
                });
                return; // Skip the rest since we handled it with subgames
            }

            // Log error for games that weren't calculated due to missing castle info
            console.error(`[${playerName}] Game not counted due to missing castle:`, g);
            return;
        }

        // Handle normal games
        if (!castleStats[playerCastle]) {
            castleStats[playerCastle] = { wins: 0, loses: 0 };
        }

        if (isWin) {
            castleStats[playerCastle].wins += 1;
        } else {
            castleStats[playerCastle].loses += 1;
        }
    });

    return castleStats;
}

/**
 * Returns the best and worst opponents for a player based on win/loss record.
 * @param {string} playerName
 * @returns {Promise<{ best: { opponent: string, wins: number, loses: number }, worst: { opponent: string, wins: number, loses: number } }>}
 */
export async function fetchBestAndWorstOpponentForPlayer(playerName) {
    const response = await fetch(`${FIREBASE_DATABASE_URL}/games/heroes3.json`);
    if (!response.ok) {
        return { best: null, worst: null };
    }
    const data = await response.json();
    if (!data) {
        return { best: null, worst: null };
    }

    // Collect all games where the player participated
    const games = Object.values(data).filter((g) => g.opponent1 === playerName || g.opponent2 === playerName);

    // Count wins and loses by opponent
    const opponentStats = {};

    games.forEach((g) => {
        const opponent = g.opponent1 === playerName ? g.opponent2 : g.opponent1;
        const isWin = g.winner === playerName;

        if (!opponent) {
            return;
        } // Skip if opponent is missing

        if (!opponentStats[opponent]) {
            opponentStats[opponent] = { wins: 0, loses: 0 };
        }

        if (isWin) {
            opponentStats[opponent].wins += 1;
        } else {
            opponentStats[opponent].loses += 1;
        }
    });

    // Find best (most wins) and worst (most loses) opponents
    let best = null,
        worst = null;
    for (const [opponent, stats] of Object.entries(opponentStats)) {
        if (!best || stats.wins > best.wins) {
            best = { opponent, wins: stats.wins, loses: stats.loses };
        }
        if (!worst || stats.loses > worst.loses) {
            worst = { opponent, wins: stats.wins, loses: stats.loses };
        }
    }

    return { best, worst };
}

export const snapshotLeaderboardRanks = async () => {
    try {
        // Fetch all users and sort by rating
        const response = await fetch(`${FIREBASE_DATABASE_URL}/users.json`);
        if (!response.ok) {
            throw new Error('Unable to fetch users for snapshot.');
        }
        const data = await response.json();
        if (!data) {
            throw new Error('No user data available.');
        }

        // Sort users by rating to get current ranking
        const playerObj = Object.entries(data)
            .map(([id, player]) => {
                let ratings = player.ratings;
                if (typeof ratings === 'string' && ratings.includes(',')) {
                    ratings = parseFloat(parseFloat(ratings.split(',').at(-1)).toFixed(2));
                } else {
                    ratings = ratings ? parseFloat(Number(ratings).toFixed(2)) : 0;
                }
                return {
                    id,
                    enteredNickname: player.enteredNickname,
                    ratings
                };
            })
            .sort((a, b) => b.ratings - a.ratings);

        const timestamp = new Date().toISOString();
        let successCount = 0;
        let errorCount = 0;

        // Update each player with their current rank
        for (let i = 0; i < playerObj.length; i++) {
            const player = playerObj[i];
            const currentRank = i + 1;

            try {
                const userResponse = await authFetch(`${FIREBASE_DATABASE_URL}/users/${player.id}.json`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                        previousRank: currentRank,
                        previousRankTimestamp: timestamp
                    }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (userResponse.ok) {
                    successCount++;
                } else {
                    errorCount++;
                }
            } catch (error) {
                console.error(`Error saving rank snapshot for ${player.enteredNickname}:`, error);
                errorCount++;
            }
        }

        // Save the snapshot timestamp in meta
        try {
            await authFetch(`${FIREBASE_DATABASE_URL}/meta/lastRankSnapshot.json`, {
                method: 'PUT',
                body: JSON.stringify(timestamp),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            console.error('Error saving meta timestamp:', error);
        }

        console.log(`Leaderboard snapshot completed: ${successCount} successful, ${errorCount} errors`);
        return { success: true, successCount, errorCount, timestamp };
    } catch (error) {
        console.error('Error snapshotting leaderboard ranks:', error);
        return { success: false, error: error.message };
    }
};
